import React, { useState, useRef, useCallback, useEffect } from 'react';
import "./css/uploads.css";

const Uploads = ({ onNext, onImageUpdate }) => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [cropMode, setCropMode] = useState(false);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState({ x: 0, y: 0 });
  
  const [cropZoom, setCropZoom] = useState(1);
  const [cropPan, setCropPan] = useState({ x: 0, y: 0 });
  
  const [showCropModal, setShowCropModal] = useState(false);
  
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, cropArea: null });
  
  const [isPdfMode, setIsPdfMode] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [selectedPageIndex, setSelectedPageIndex] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingPageSelection, setPendingPageSelection] = useState(null);
  const [autoSelectedPage, setAutoSelectedPage] = useState(null);
  
  const [originalPdfFile, setOriginalPdfFile] = useState(null);
  const [originalPdfPageNumber, setOriginalPdfPageNumber] = useState(null);
  
  const EXPORT_QUALITY = 1.0;
  const PDF_RENDER_SCALE = 4;
  const PDF_PREVIEW_SCALE = 1.5;
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);
  const autoScrollInterval = useRef(null);
  const cropModalRef = useRef(null);
  const cropImageRef = useRef(null);

  const handleAutoScroll = useCallback((mouseY) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const scrollZone = 50;
    const scrollSpeed = 5;

    if (mouseY < rect.top + scrollZone) {
      container.scrollTop -= scrollSpeed;
    } else if (mouseY > rect.bottom - scrollZone) {
      container.scrollTop += scrollSpeed;
    }
  }, []);

  useEffect(() => {
    const dropArea = dropAreaRef.current;
    if (!dropArea) return;

    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const highlight = () => {
      dropArea.classList.add('drag-over');
    };

    const unhighlight = () => {
      dropArea.classList.remove('drag-over');
    };

    const handleDrop = (e) => {
      preventDefaults(e);
      unhighlight();
      
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    };

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropArea.addEventListener(eventName, preventDefaults, false);
    });

    ['dragenter', 'dragover'].forEach(eventName => {
      dropArea.addEventListener(eventName, highlight, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
      dropArea.addEventListener(eventName, unhighlight, false);
    });

    dropArea.addEventListener('drop', handleDrop, false);

    return () => {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        dropArea.removeEventListener(eventName, preventDefaults, false);
      });

      ['dragenter', 'dragover'].forEach(eventName => {
        dropArea.removeEventListener(eventName, highlight, false);
      });

      ['dragleave', 'drop'].forEach(eventName => {
        dropArea.removeEventListener(eventName, unhighlight, false);
      });

      dropArea.removeEventListener('drop', handleDrop, false);
    };
  }, []);

  const loadPdfJs = useCallback(async () => {
    if (window.pdfjsLib) return window.pdfjsLib;
    
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    document.head.appendChild(script);
    
    await new Promise((resolve, reject) => {
      script.onload = resolve;
      script.onerror = reject;
    });
    
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    
    return window.pdfjsLib;
  }, []);

  const convertPdfPageToImage = useCallback(async (file, pageNumber, scale = PDF_RENDER_SCALE) => {
    try {
      setIsLoadingPdf(true);
      
      const pdfjsLib = await loadPdfJs();
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
      
      if (pageNumber === 'all') {
        const pages = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          // Thumbnails only: cap the longest side so a huge page (e.g. an A0 plan) does not make a giant canvas.
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: Math.min(PDF_PREVIEW_SCALE, 1600 / Math.max(base.width, base.height)) });
          
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = 'high';
          
          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;
          
          pages.push({
            pageNumber: i,
            imageUrl: canvas.toDataURL('image/png', 0.9),
            width: viewport.width,
            height: viewport.height,
            aspectRatio: viewport.width / viewport.height
          });
        }
        
        setPdfPages(pages);
        
        if (pages.length === 1) {
          setAutoSelectedPage(pages[0]);
          // Awaited on purpose: this used to run in the background while the loader was already switched off,
          // so the empty upload screen stayed visible for seconds before the crop page suddenly appeared.
          await handleAutoSelectPage(pages[0], 0, file);
        } else {
          setIsPdfMode(true);
        }
        
        setIsLoadingPdf(false);
        return pages;
      } else {
        const page = await pdf.getPage(pageNumber);
        // 4x of a big drawing sheet (this one: 2384 x 3370 pt) meant a ~128-million-pixel canvas that took many
        // seconds to draw and encode. Keep the longest side <= 5000 px and the total <= 20 MP; normal A4 pages
        // still render at the full 4x.
        const base = page.getViewport({ scale: 1 });
        const safeScale = Math.min(
          scale,
          5000 / Math.max(base.width, base.height),
          Math.sqrt(20e6 / (base.width * base.height))
        );
        const viewport = page.getViewport({ scale: safeScale });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        const imageUrl = canvas.toDataURL('image/png', EXPORT_QUALITY);
        setIsLoadingPdf(false);
        
        return {
          imageUrl,
          pageNumber,
          originalFileName: file.name,
          width: viewport.width,
          height: viewport.height
        };
      }
    } catch (error) {
      console.error('Error converting PDF:', error);
      setIsLoadingPdf(false);
      alert('Error processing PDF. Please try again.');
      return null;
    }
  }, [loadPdfJs]);

  const handleAutoSelectPage = async (page, index, file) => {
    setSelectedPageIndex(index);
    
    try {
      const originalFile = file || fileInputRef.current?.files[0];
      if (!originalFile) {
        throw new Error('No file found');
      }
      
      setOriginalPdfFile(originalFile);
      setOriginalPdfPageNumber(page.pageNumber);
      
      const highQualityPage = await convertPdfPageToImage(
        originalFile,
        page.pageNumber,
        PDF_RENDER_SCALE
      );
      
      if (highQualityPage) {
        setUploadedImage(highQualityPage.imageUrl);
        setOriginalImage(highQualityPage.imageUrl);
        
        const newImageData = {
          name: `Page_${page.pageNumber}.png`,
          size: 'PDF Page',
          type: 'image/png',
          isFromPdf: true,
          pageNumber: page.pageNumber,
          originalFileName: originalFile.name,
          width: highQualityPage.width,
          height: highQualityPage.height,
          originalWidth: highQualityPage.width,
          originalHeight: highQualityPage.height
        };
        
        setImageData(newImageData);

        if (onImageUpdate) {
          onImageUpdate(highQualityPage.imageUrl, newImageData);
        }

        resetZoomAndPosition();
      }
    } catch (error) {
      console.error('Error in auto page selection:', error);
      setUploadedImage(page.imageUrl);
      setOriginalImage(page.imageUrl);
      
      const newImageData = {
        name: `Page_${page.pageNumber}.png`,
        size: 'PDF Page',
        type: 'image/png',
        isFromPdf: true,
        pageNumber: page.pageNumber,
        width: page.width,
        height: page.height,
        originalWidth: page.width,
        originalHeight: page.height
      };
      
      setImageData(newImageData);

      if (onImageUpdate) {
        onImageUpdate(page.imageUrl, newImageData);
      }

      resetZoomAndPosition();
    }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    localStorage.removeItem('entrance_analysis_report_data');
    localStorage.removeItem('space_analysis_report_data');
    
    if (file.type === 'application/pdf') {
      setOriginalPdfFile(file);
      const pages = await convertPdfPageToImage(file, 'all');
      if (pages && pages.length > 0) {
        if (pages.length > 1) {
          setIsPdfMode(true);
        }
      }
    } else if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        setUploadedImage(imageUrl);
        setOriginalImage(imageUrl);
        
        const img = new Image();
        img.onload = () => {
          const newImageData = {
            name: file.name,
            size: (file.size / 1024).toFixed(2) + ' KB',
            type: file.type,
            width: img.width,
            height: img.height,
            originalWidth: img.width,
            originalHeight: img.height
          };
          
          setImageData(newImageData);
          
          if (onImageUpdate) {
            onImageUpdate(imageUrl, newImageData);
          }
        };
        img.src = imageUrl;
        
        resetImageState();
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload an image or PDF file.');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    handleFileSelect(file);
  };

  const resetImageState = () => {
    setZoom(1);
    setImageRotation(0);
    setPosition({ x: 0, y: 0 });
    setCropMode(false);
    setCropArea({ x: 0, y: 0, width: 0, height: 0 });
    setIsPdfMode(false);
    setPdfPages([]);
    setSelectedPageIndex(null);
    setAutoSelectedPage(null);
    setCropZoom(1);
    setCropPan({ x: 0, y: 0 });
  };

  const handlePageSelect = async (pageIndex) => {
    const selectedPage = pdfPages[pageIndex];
    if (!selectedPage) return;

    setPendingPageSelection({ pageIndex, selectedPage });
    setShowConfirmDialog(true);
  };

  const confirmPageSelection = async () => {
    if (!pendingPageSelection) return;

    const { pageIndex, selectedPage } = pendingPageSelection;

    setSelectedPageIndex(pageIndex);
    
    try {
      const originalFile = originalPdfFile || fileInputRef.current?.files[0];
      if (!originalFile) {
        throw new Error('No file found');
      }
      
      setOriginalPdfPageNumber(selectedPage.pageNumber);
      
      const highQualityPage = await convertPdfPageToImage(
        originalFile,
        selectedPage.pageNumber,
        PDF_RENDER_SCALE
      );
      
      if (highQualityPage) {
        setUploadedImage(highQualityPage.imageUrl);
        setOriginalImage(highQualityPage.imageUrl);
        
        const newImageData = {
          name: `Page_${selectedPage.pageNumber}.png`,
          size: 'PDF Page',
          type: 'image/png',
          isFromPdf: true,
          pageNumber: selectedPage.pageNumber,
          originalFileName: originalFile.name,
          width: highQualityPage.width,
          height: highQualityPage.height,
          originalWidth: highQualityPage.width,
          originalHeight: highQualityPage.height
        };
        
        setImageData(newImageData);

        if (onImageUpdate) {
          onImageUpdate(highQualityPage.imageUrl, newImageData);
        }

        setIsPdfMode(false);
        resetZoomAndPosition();
      }
    } catch (error) {
      console.error('Error in page selection:', error);
      setUploadedImage(selectedPage.imageUrl);
      setOriginalImage(selectedPage.imageUrl);
      
      const newImageData = {
        name: `Page_${selectedPage.pageNumber}.png`,
        size: 'PDF Page',
        type: 'image/png',
        isFromPdf: true,
        pageNumber: selectedPage.pageNumber,
        width: selectedPage.width,
        height: selectedPage.height,
        originalWidth: selectedPage.width,
        originalHeight: selectedPage.height
      };
      
      setImageData(newImageData);

      if (onImageUpdate) {
        onImageUpdate(selectedPage.imageUrl, newImageData);
      }

      setIsPdfMode(false);
      resetZoomAndPosition();
    }

    setShowConfirmDialog(false);
    setPendingPageSelection(null);
  };

  const cancelPageSelection = () => {
    setShowConfirmDialog(false);
    setPendingPageSelection(null);
  };

  const resetZoomAndPosition = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setCropMode(false);
    setCropArea({ x: 0, y: 0, width: 0, height: 0 });
    setCropZoom(1);
    setCropPan({ x: 0, y: 0 });
  };

  const handleBackToPdfSelection = () => {
    setIsPdfMode(true);
    setUploadedImage(null);
    setOriginalImage(null);
    setImageData(null);
    resetZoomAndPosition();
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.05, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.05, 0.1));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleRotate = useCallback(() => {
    const newRotation = (imageRotation + 90) % 360;
    setImageRotation(newRotation);

    // Apply rotation to the actual image data so Next passes a rotated image
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const isLandscape = newRotation === 90 || newRotation === 270;
      canvas.width  = isLandscape ? img.height : img.width;
      canvas.height = isLandscape ? img.width  : img.height;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((newRotation * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      const rotatedUrl = canvas.toDataURL('image/png', 1.0);
      setUploadedImage(rotatedUrl);
      setOriginalImage(rotatedUrl);
      const newData = {
        ...imageData,
        width:  canvas.width,
        height: canvas.height,
        originalWidth:  canvas.width,
        originalHeight: canvas.height,
      };
      setImageData(newData);
      if (onImageUpdate) onImageUpdate(rotatedUrl, newData);
      setImageRotation(0); // already baked into the image
    };
    img.src = uploadedImage;
  }, [imageRotation, uploadedImage, imageData, onImageUpdate]);

  const handleCropZoomIn = () => {
    setCropZoom(prev => {
      const newZoom = Math.min(prev + 0.05, 4);
      if (!resizeHandle && !isCropping) {
        setCropArea({ x: 0, y: 0, width: 0, height: 0 });
      }
      return newZoom;
    });
  };

  const handleCropZoomOut = () => {
    setCropZoom(prev => {
      const newZoom = Math.max(prev - 0.05, 0.5);
      if (!resizeHandle && !isCropping) {
        setCropArea({ x: 0, y: 0, width: 0, height: 0 });
      }
      return newZoom;
    });
  };

  const handleCropZoomReset = () => {
    setCropZoom(1);
    setCropPan({ x: 0, y: 0 });
    setCropArea({ x: 0, y: 0, width: 0, height: 0 });
    setIsCropping(false);
    setResizeHandle(null);
  };

  // Works for both mouse and touch events — a touch event has no top-level
  // clientX/clientY (that lives on e.touches[0]/e.changedTouches[0]), so
  // reading e.clientX directly on mobile always produced NaN and the whole
  // crop drag/resize silently did nothing.
  const getEventPoint = (e) => {
    if (e.touches && e.touches.length > 0) return e.touches[0];
    if (e.changedTouches && e.changedTouches.length > 0) return e.changedTouches[0];
    return e;
  };

  const getMousePosition = (e, containerElement, imageElement) => {
    if (!containerElement || !imageElement) return { x: 0, y: 0 };

    const point = getEventPoint(e);
    const imgRect = imageElement.getBoundingClientRect();

    const x = point.clientX - imgRect.left;
    const y = point.clientY - imgRect.top;

    return { x, y };
  };

  const getResizeHandle = (mousePos) => {
    const handleSize = 20;
    const edgeSize = 12;
    
    const { x, y, width, height } = cropArea;
    
    if (Math.abs(mousePos.x - x) < handleSize && Math.abs(mousePos.y - y) < handleSize) {
      return 'tl';
    }
    if (Math.abs(mousePos.x - (x + width)) < handleSize && Math.abs(mousePos.y - y) < handleSize) {
      return 'tr';
    }
    if (Math.abs(mousePos.x - x) < handleSize && Math.abs(mousePos.y - (y + height)) < handleSize) {
      return 'bl';
    }
    if (Math.abs(mousePos.x - (x + width)) < handleSize && Math.abs(mousePos.y - (y + height)) < handleSize) {
      return 'br';
    }
    
    if (Math.abs(mousePos.y - y) < edgeSize && mousePos.x > x + handleSize && mousePos.x < x + width - handleSize) {
      return 't';
    }
    if (Math.abs(mousePos.y - (y + height)) < edgeSize && mousePos.x > x + handleSize && mousePos.x < x + width - handleSize) {
      return 'b';
    }
    if (Math.abs(mousePos.x - x) < edgeSize && mousePos.y > y + handleSize && mousePos.y < y + height - handleSize) {
      return 'l';
    }
    if (Math.abs(mousePos.x - (x + width)) < edgeSize && mousePos.y > y + handleSize && mousePos.y < y + height - handleSize) {
      return 'r';
    }
    
    if (mousePos.x >= x + handleSize && mousePos.x <= x + width - handleSize && 
        mousePos.y >= y + handleSize && mousePos.y <= y + height - handleSize) {
      return 'move';
    }
    
    return null;
  };

  const handleMouseDown = (e) => {
    if (!cropMode || !showCropModal) return;
    
    const imgElement = cropImageRef.current;
    const containerElement = cropModalRef.current;
    
    if (!imgElement || !containerElement) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const mousePos = getMousePosition(e, containerElement, imgElement);
    const imgRect = imgElement.getBoundingClientRect();
    
    if (mousePos.x < 0 || mousePos.x > imgRect.width || mousePos.y < 0 || mousePos.y > imgRect.height) {
      return;
    }
    
    if (cropArea.width > 0 && cropArea.height > 0) {
      const handle = getResizeHandle(mousePos);
      if (handle) {
        setResizeHandle(handle);
        setResizeStart({
          x: mousePos.x,
          y: mousePos.y,
          cropArea: { ...cropArea }
        });
        return;
      }
    }
    
    setIsCropping(true);
    setCropStart(mousePos);
    setCropArea({ 
      x: mousePos.x, 
      y: mousePos.y, 
      width: 0, 
      height: 0 
    });
  };

  const handleMouseMove = (e) => {
    if (!cropMode || !showCropModal) return;
    if (!isCropping && !resizeHandle) return;
    
    const imgElement = cropImageRef.current;
    const containerElement = cropModalRef.current;
    
    if (!imgElement || !containerElement) return;
    
    const mousePos = getMousePosition(e, containerElement, imgElement);
    const imgRect = imgElement.getBoundingClientRect();
    const maxX = imgRect.width;
    const maxY = imgRect.height;
    
    if (resizeHandle && resizeStart.cropArea) {
      e.preventDefault();
      e.stopPropagation();
      
      const deltaX = mousePos.x - resizeStart.x;
      const deltaY = mousePos.y - resizeStart.y;
      const original = resizeStart.cropArea;
      
      let newCrop = { ...original };
      
      switch (resizeHandle) {
        case 'tl':
          newCrop.x = Math.max(0, Math.min(mousePos.x, original.x + original.width - 20));
          newCrop.y = Math.max(0, Math.min(mousePos.y, original.y + original.height - 20));
          newCrop.width = original.x + original.width - newCrop.x;
          newCrop.height = original.y + original.height - newCrop.y;
          break;
        case 'tr':
          newCrop.y = Math.max(0, Math.min(mousePos.y, original.y + original.height - 20));
          newCrop.width = Math.max(20, Math.min(mousePos.x - original.x, maxX - original.x));
          newCrop.height = original.y + original.height - newCrop.y;
          break;
        case 'bl':
          newCrop.x = Math.max(0, Math.min(mousePos.x, original.x + original.width - 20));
          newCrop.width = original.x + original.width - newCrop.x;
          newCrop.height = Math.max(20, Math.min(mousePos.y - original.y, maxY - original.y));
          break;
        case 'br':
          newCrop.width = Math.max(20, Math.min(mousePos.x - original.x, maxX - original.x));
          newCrop.height = Math.max(20, Math.min(mousePos.y - original.y, maxY - original.y));
          break;
        case 't':
          newCrop.y = Math.max(0, Math.min(mousePos.y, original.y + original.height - 20));
          newCrop.height = original.y + original.height - newCrop.y;
          break;
        case 'b':
          newCrop.height = Math.max(20, Math.min(mousePos.y - original.y, maxY - original.y));
          break;
        case 'l':
          newCrop.x = Math.max(0, Math.min(mousePos.x, original.x + original.width - 20));
          newCrop.width = original.x + original.width - newCrop.x;
          break;
        case 'r':
          newCrop.width = Math.max(20, Math.min(mousePos.x - original.x, maxX - original.x));
          break;
        case 'move':
          const newX = original.x + deltaX;
          const newY = original.y + deltaY;
          newCrop.x = Math.max(0, Math.min(newX, maxX - original.width));
          newCrop.y = Math.max(0, Math.min(newY, maxY - original.height));
          break;
        default:
          break;
      }
      
      setCropArea(newCrop);
    } 
    else if (isCropping) {
      e.preventDefault();
      e.stopPropagation();
      
      const width = mousePos.x - cropStart.x;
      const height = mousePos.y - cropStart.y;
      
      const newX = width < 0 ? Math.max(0, mousePos.x) : cropStart.x;
      const newY = height < 0 ? Math.max(0, mousePos.y) : cropStart.y;
      const newWidth = Math.min(Math.abs(width), maxX - newX);
      const newHeight = Math.min(Math.abs(height), maxY - newY);
      
      setCropArea({
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsCropping(false);
    setResizeHandle(null);
    setResizeStart({ x: 0, y: 0, cropArea: null });
    
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
  };

  const handleCropToggle = () => {
    if (!cropMode) {
      setCropMode(true);
      setShowCropModal(true);
      setCropArea({ x: 0, y: 0, width: 0, height: 0 });
      setCropZoom(1);
      setCropPan({ x: 0, y: 0 });
    } else {
      setCropMode(false);
      setShowCropModal(false);
      setCropArea({ x: 0, y: 0, width: 0, height: 0 });
    }
    setIsCropping(false);
    setResizeHandle(null);
  };

  const applyCropHighQuality = useCallback(async () => {
    const imgElement = cropImageRef.current;
    
    if (!imgElement || cropArea.width === 0 || cropArea.height === 0) return;

    const sourceImage = originalImage || uploadedImage;
    
    const tempImg = new Image();
    tempImg.crossOrigin = 'anonymous';
    
    return new Promise((resolve, reject) => {
      tempImg.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const displayedRect = imgElement.getBoundingClientRect();
        
        const scaleX = tempImg.naturalWidth / displayedRect.width;
        const scaleY = tempImg.naturalHeight / displayedRect.height;
        
        const cropX = Math.round(cropArea.x * scaleX);
        const cropY = Math.round(cropArea.y * scaleY);
        const cropWidth = Math.round(cropArea.width * scaleX);
        const cropHeight = Math.round(cropArea.height * scaleY);
        
        const boundedCropX = Math.max(0, Math.min(cropX, tempImg.naturalWidth - 1));
        const boundedCropY = Math.max(0, Math.min(cropY, tempImg.naturalHeight - 1));
        const boundedCropWidth = Math.max(1, Math.min(cropWidth, tempImg.naturalWidth - boundedCropX));
        const boundedCropHeight = Math.max(1, Math.min(cropHeight, tempImg.naturalHeight - boundedCropY));
        
        canvas.width = boundedCropWidth;
        canvas.height = boundedCropHeight;
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.drawImage(
          tempImg,
          boundedCropX, boundedCropY, boundedCropWidth, boundedCropHeight,
          0, 0, boundedCropWidth, boundedCropHeight
        );
        
        const croppedImageUrl = canvas.toDataURL('image/png', EXPORT_QUALITY);
        
        setUploadedImage(croppedImageUrl);
        setOriginalImage(croppedImageUrl);
        
        const newImageData = {
          ...imageData,
          name: imageData?.name?.replace(/\.[^/.]+$/, "") + "_cropped.png" || "cropped_image.png",
          width: boundedCropWidth,
          height: boundedCropHeight,
          originalWidth: boundedCropWidth,
          originalHeight: boundedCropHeight
        };
        setImageData(newImageData);
        
        if (onImageUpdate) {
          onImageUpdate(croppedImageUrl, newImageData);
        }
        
        setCropMode(false);
        setShowCropModal(false);
        setCropArea({ x: 0, y: 0, width: 0, height: 0 });
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setCropZoom(1);
        setCropPan({ x: 0, y: 0 });
        
        resolve(croppedImageUrl);
      };
      
      tempImg.onerror = () => {
        reject(new Error('Failed to load image for cropping'));
      };
      
      tempImg.src = sourceImage;
    });
  }, [cropArea, imageData, onImageUpdate, originalImage, uploadedImage]);

  const [isApplyingCrop, setIsApplyingCrop] = useState(false);
  // True from the moment Next / "Crop & Next" is pressed until the editor opens. Creating the
  // project takes a moment; without this the screen just showed the cropped picture with a fresh
  // "Next" button again, so people pressed Next a second time. Now one press = one overlay.
  const [isNavigating, setIsNavigating] = useState(false);
  const goNext = useCallback(async (image) => {
    if (!onNext || isNavigating) return;
    setIsNavigating(true);
    try {
      await onNext(image);
    } finally {
      // Normally this component is already gone (editor opened). If Next did nothing
      // (e.g. an error alert), let the user try again after a few seconds.
      setTimeout(() => setIsNavigating(false), 4000);
    }
  }, [onNext, isNavigating]);

  const applyCropAndNext = useCallback(async () => {
    if (isApplyingCrop) return; // guard against double-click while processing
    setIsApplyingCrop(true);
    try {
      // applyCropHighQuality() already calls onImageUpdate(croppedImageUrl, ...) which
      // sets Editor.js's `finalImage` state — but that update is async and hasn't been
      // committed/re-rendered yet by the time we call onNext() a moment later. onNext
      // (Editor.js's handleNext) was captured from the PREVIOUS render and still closes
      // over the pre-crop `finalImage`, so the project got created with the original
      // uncropped image. Passing the cropped URL through explicitly sidesteps the stale
      // closure entirely instead of depending on state-update timing.
      const croppedImageUrl = await applyCropHighQuality();
      await goNext(croppedImageUrl);
    } catch (error) {
      console.error('Error applying crop:', error);
      alert('Error cropping image. Please try again.');
    } finally {
      setIsApplyingCrop(false);
    }
  }, [applyCropHighQuality, goNext, isApplyingCrop]);

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setOriginalImage(null);
    setImageData(null);
    setOriginalPdfFile(null);
    setOriginalPdfPageNumber(null);
    resetImageState();
    if (onImageUpdate) {
      onImageUpdate(null, null);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const getCursorStyle = () => {
    if (!cropMode) return isDragging ? 'grabbing' : 'grab';
    if (isCropping) return 'crosshair';
    
    switch (resizeHandle) {
      case 'tl':
      case 'br':
        return 'nwse-resize';
      case 'tr':
      case 'bl':
        return 'nesw-resize';
      case 't':
      case 'b':
        return 'ns-resize';
      case 'l':
      case 'r':
        return 'ew-resize';
      case 'move':
        return 'move';
      default:
        return 'crosshair';
    }
  };

  const renderCropOverlay = () => {
    if (!cropMode || cropArea.width === 0 || cropArea.height === 0) return null;
    
    return (
      <>
        <div 
          style={{
            position: 'absolute',
            left: cropArea.x,
            top: cropArea.y,
            width: cropArea.width,
            height: cropArea.height,
            border: '2px solid #00A19D',
            backgroundColor: 'rgba(0, 161, 157, 0.1)',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Grid lines */}
          <div style={{
            position: 'absolute',
            top: '33.33%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)'
          }} />
          <div style={{
            position: 'absolute',
            top: '66.66%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)'
          }} />
          <div style={{
            position: 'absolute',
            left: '33.33%',
            top: 0,
            bottom: 0,
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)'
          }} />
          <div style={{
            position: 'absolute',
            left: '66.66%',
            top: 0,
            bottom: 0,
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)'
          }} />
          
          {/* Corner handles */}
          {['tl', 'tr', 'bl', 'br'].map((corner) => {
            const positions = {
              tl: { top: '-8px', left: '-8px' },
              tr: { top: '-8px', right: '-8px' },
              bl: { bottom: '-8px', left: '-8px' },
              br: { bottom: '-8px', right: '-8px' }
            };
            const cursors = {
              tl: 'nwse-resize',
              tr: 'nesw-resize',
              bl: 'nesw-resize',
              br: 'nwse-resize'
            };
            
            return (
              <div
                key={corner}
                style={{
                  position: 'absolute',
                  ...positions[corner],
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#00A19D',
                  border: '3px solid #ffffff',
                  borderRadius: '50%',
                  pointerEvents: 'auto',
                  cursor: cursors[corner],
                  zIndex: 10,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.4)'
                }}
              />
            );
          })}
          
          {/* Edge handles */}
          <div style={{
            position: 'absolute',
            top: '-6px',
            left: '25px',
            right: '25px',
            height: '12px',
            pointerEvents: 'auto',
            cursor: 'ns-resize'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-6px',
            left: '25px',
            right: '25px',
            height: '12px',
            pointerEvents: 'auto',
            cursor: 'ns-resize'
          }} />
          <div style={{
            position: 'absolute',
            left: '-6px',
            top: '25px',
            bottom: '25px',
            width: '12px',
            pointerEvents: 'auto',
            cursor: 'ew-resize'
          }} />
          <div style={{
            position: 'absolute',
            right: '-6px',
            top: '25px',
            bottom: '25px',
            width: '12px',
            pointerEvents: 'auto',
            cursor: 'ew-resize'
          }} />
          
          {/* Dimensions label */}
          <div style={{
            position: 'absolute',
            bottom: '-30px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: '#ffffff',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none'
          }}>
            {Math.round(cropArea.width)} × {Math.round(cropArea.height)} px
          </div>
        </div>
      </>
    );
  };

  if (isPdfMode && pdfPages.length > 0) {
    return (
      <>
        <div className="uploads-container">
          <div className="pdf-header">
            <h4>
              <i className="bi bi-file-earmark-pdf me-2"></i>
              Select a Page from PDF
            </h4>
            <p>Choose the page you want to use as your floor plan ({pdfPages.length} pages found)</p>
          </div>

          <div className="pdf-pages-grid">
            {pdfPages.map((page, index) => (
              <div
                key={index}
                className={`pdf-page-card ${selectedPageIndex === index ? 'selected' : ''}`}
                onClick={() => handlePageSelect(index)}
              >
                <div className="page-preview">
                  <img src={page.imageUrl} alt={`Page ${page.pageNumber}`} />
                  <div className="page-overlay">
                    <i className="bi bi-check-circle-fill"></i>
                  </div>
                </div>
                <div className="page-info">
                  <span className="page-number">Page {page.pageNumber}</span>
                  <span className="page-dimensions">
                    {Math.round(page.width)}×{Math.round(page.height)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="pdf-actions">
            <button className="control-btn" onClick={handleRemoveImage}>
              <i className="bi bi-arrow-left me-2"></i>
              Choose Different File
            </button>
          </div>

          {showConfirmDialog && pendingPageSelection && (
            <div className="confirm-overlay">
              <div className="confirm-dialog">
                <div className="confirm-header">
                  <i className="bi bi-question-circle-fill"></i>
                  <h5>Confirm Page Selection</h5>
                </div>
                
                <div className="confirm-content">
                  <div className="confirm-preview">
                    <img 
                      src={pendingPageSelection.selectedPage.imageUrl} 
                      alt={`Page ${pendingPageSelection.selectedPage.pageNumber}`}
                    />
                  </div>
                  <div className="confirm-text">
                    <p>Are you sure you want to select <strong>Page {pendingPageSelection.selectedPage.pageNumber}</strong> for your floor plan?</p>
                  </div>
                </div>
                
                <div className="confirm-actions">
                  <button className="confirm-btn cancel" onClick={cancelPageSelection}>
                    <i className="bi bi-x-circle me-2"></i>
                    Cancel
                  </button>
                  <button className="confirm-btn confirm" onClick={confirmPageSelection}>
                    <i className="bi bi-check-circle me-2"></i>
                    Select Page
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <style jsx>{`
          .pdf-header {
            text-align: center;
            margin-bottom: 24px;
          }

          .pdf-header h4 {
            color: #ffffff;
            margin-bottom: 8px;
            font-weight: 600;
          }

          .pdf-header p {
            color: #cccccc;
            margin: 0;
          }

          .pdf-pages-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 16px;
            margin-bottom: 24px;
            max-height: calc(100vh - 200px);
            overflow-y: auto;
            padding: 16px;
          }

          .pdf-page-card {
            background: #ffffff;
            border: 2px solid #e5e5e5;
            border-radius: 8px;
            padding: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
          }

          .pdf-page-card:hover {
            border-color: #00A19D;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 161, 157, 0.2);
          }

          .pdf-page-card.selected {
            border-color: #00A19D;
            background: rgba(0, 161, 157, 0.05);
          }

          .page-preview {
            position: relative;
            margin-bottom: 12px;
            border-radius: 6px;
            overflow: hidden;
            background: #f5f5f5;
          }

          .page-preview img {
            width: 100%;
            height: auto;
            display: block;
            max-height: 200px;
            object-fit: contain;
          }

          .page-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 161, 157, 0.9);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .pdf-page-card.selected .page-overlay,
          .pdf-page-card:hover .page-overlay {
            opacity: 1;
          }

          .page-overlay i {
            font-size: 2rem;
            color: #ffffff;
          }

          .page-info {
            text-align: center;
          }

          .page-number {
            display: block;
            color: #333333;
            font-weight: 600;
            margin-bottom: 4px;
          }

          .page-dimensions {
            display: block;
            color: #666666;
            font-size: 0.875rem;
          }

          .pdf-actions {
            display: flex;
            justify-content: center;
            padding: 16px;
          }

          .confirm-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2000;
            animation: fadeIn 0.3s ease;
          }

          .confirm-dialog {
            background: #ffffff;
            border-radius: 12px;
            padding: 24px;
            max-width: 500px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 40px rgba(0, 161, 157, 0.2);
            animation: slideIn 0.3s ease;
            border: 1px solid #00A19D;
          }

          .confirm-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
            padding-bottom: 16px;
            border-bottom: 1px solid #e5e5e5;
          }

          .confirm-header i {
            font-size: 1.5rem;
            color: #00A19D;
          }

          .confirm-header h5 {
            color: #333333;
            margin: 0;
            font-weight: 600;
          }

          .confirm-content {
            margin-bottom: 24px;
          }

          .confirm-preview {
            text-align: center;
            margin-bottom: 16px;
          }

          .confirm-preview img {
            max-width: 200px;
            max-height: 150px;
            border-radius: 8px;
            border: 2px solid #e5e5e5;
            object-fit: contain;
            background: #f5f5f5;
          }

          .confirm-text p {
            color: #333333;
            margin-bottom: 8px;
            line-height: 1.5;
          }

          .confirm-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
          }

          .confirm-btn {
            padding: 10px 20px;
            border-radius: 8px;
            border: none;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s ease;
            font-size: 0.875rem;
          }

          .confirm-btn.cancel {
            background: #f0f0f0;
            color: #333333;
            border: 1px solid #e5e5e5;
          }

          .confirm-btn.cancel:hover {
            background: #e5e5e5;
          }

          .confirm-btn.confirm {
            background: #00A19D;
            color: #ffffff;
          }

          .confirm-btn.confirm:hover {
            background: #00857E;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideIn {
            from { 
              opacity: 0;
              transform: translateY(-20px) scale(0.95);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @media (max-width: 768px) {
            .pdf-pages-grid {
              grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
              gap: 12px;
              padding: 12px;
            }

            .page-preview img {
              max-height: 150px;
            }
          }
        `}</style>
      </>
    );
  }

  if (isLoadingPdf) {
    return (
      <>
        <div className="uploads-container">
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <style>{`
              .ppu-wrap{display:flex;flex-direction:column;align-items:center;padding:32px 16px;gap:24px;}
              .ppu-stage{position:relative;width:220px;height:290px;}
              .ppu-doc{position:absolute;left:0;top:0;width:220px;height:290px;background:#fff;border-radius:10px;border:1.5px solid #B5D4F4;box-shadow:0 4px 24px rgba(0,161,157,0.13);overflow:hidden;}
              .ppu-header{background:#00A19D;height:38px;display:flex;align-items:center;padding:0 14px;gap:8px;}
              .ppu-label{color:#fff;font-family:'Inter',sans-serif;font-size:13px;font-weight:500;letter-spacing:1px;}
              .ppu-icon{width:18px;height:22px;background:#fff;border-radius:2px;display:flex;align-items:center;justify-content:center;}
              .ppu-icon span{color:#00A19D;font-size:7px;font-weight:700;font-family:monospace;}
              .ppu-lines{padding:18px 18px 0;}
              .ppu-line{height:9px;background:#E1F5EE;border-radius:4px;margin-bottom:10px;}
              .ppu-w100{width:100%;}.ppu-w80{width:80%;}.ppu-w90{width:90%;}.ppu-w60{width:60%;}.ppu-w70{width:70%;}.ppu-w85{width:85%;}
              .ppu-block{margin:14px 18px 0;height:60px;background:#E1F5EE;border-radius:6px;position:relative;overflow:hidden;}
              .ppu-shimmer{position:absolute;inset:0;background:linear-gradient(90deg,transparent 0%,rgba(0,233,179,0.35) 50%,transparent 100%);animation:ppuShimmer 1.6s linear infinite;}
              .ppu-beam{position:absolute;left:0;top:0;width:100%;height:3px;border-radius:2px;background:linear-gradient(90deg,transparent 0%,#00E9B3 30%,#16B5D9 60%,#2B81FF 85%,transparent 100%);animation:ppuScan 2s linear infinite;z-index:10;}
              .ppu-glow{position:absolute;left:0;top:-10px;width:100%;height:24px;background:linear-gradient(180deg,transparent,rgba(0,233,179,0.18) 40%,rgba(22,181,217,0.22) 60%,transparent);animation:ppuScan 2s linear infinite;z-index:9;}
              .ppu-overlay{position:absolute;left:0;top:0;width:100%;height:0;background:linear-gradient(180deg,rgba(0,161,157,0.1),rgba(230,241,251,0.35));animation:ppuFill 2s linear infinite;z-index:8;border-radius:10px 10px 0 0;}
              .ppu-corner{position:absolute;width:18px;height:18px;border-color:#00A19D;border-style:solid;border-width:0;}
              .ppu-tl{top:-2px;left:-2px;border-top-width:3px;border-left-width:3px;border-radius:4px 0 0 0;}
              .ppu-tr{top:-2px;right:-2px;border-top-width:3px;border-right-width:3px;border-radius:0 4px 0 0;}
              .ppu-bl{bottom:-2px;left:-2px;border-bottom-width:3px;border-left-width:3px;border-radius:0 0 0 4px;}
              .ppu-br{bottom:-2px;right:-2px;border-bottom-width:3px;border-right-width:3px;border-radius:0 0 4px 0;}
              .ppu-status-row{display:flex;align-items:center;gap:10px;}
              .ppu-status-dot{width:9px;height:9px;border-radius:50%;background:#00A19D;animation:ppuBlink 1s ease-in-out infinite;}
              .ppu-status-text{font-family:'Inter',sans-serif;font-size:13px;color:#8A94A6;}
              .ppu-progress-wrap{width:220px;height:6px;background:#ECEEF2;border-radius:6px;overflow:hidden;}
              .ppu-progress-bar{height:100%;border-radius:6px;background:linear-gradient(90deg,#00A19D,#16B5D9,#2B81FF);animation:ppuProgress 2s linear infinite;}
              @keyframes ppuScan{0%{transform:translateY(0);}100%{transform:translateY(290px);}}
              @keyframes ppuFill{0%{height:0;}100%{height:290px;}}
              @keyframes ppuShimmer{0%{transform:translateX(-100%);}100%{transform:translateX(200%);}}
              @keyframes ppuBlink{0%,100%{opacity:1;}50%{opacity:0.3;}}
              @keyframes ppuProgress{0%{width:0%;}90%{width:100%;}100%{width:100%;}}
            `}</style>
            <div className="ppu-wrap">
              <div className="ppu-stage">
                <div className="ppu-doc">
                  <div className="ppu-header">
                    <div className="ppu-icon"><span>PDF</span></div>
                    <span className="ppu-label">processing...</span>
                  </div>
                  <div className="ppu-lines">
                    <div className="ppu-line ppu-w100"></div>
                    <div className="ppu-line ppu-w80"></div>
                    <div className="ppu-line ppu-w90"></div>
                    <div className="ppu-line ppu-w60"></div>
                  </div>
                  <div className="ppu-block"><div className="ppu-shimmer"></div></div>
                  <div className="ppu-lines" style={{paddingTop:'14px'}}>
                    <div className="ppu-line ppu-w85"></div>
                    <div className="ppu-line ppu-w70"></div>
                    <div className="ppu-line ppu-w90"></div>
                  </div>
                </div>
                <div className="ppu-overlay"></div>
                <div className="ppu-glow"></div>
                <div className="ppu-beam"></div>
                <div className="ppu-corner ppu-tl"></div>
                <div className="ppu-corner ppu-tr"></div>
                <div className="ppu-corner ppu-bl"></div>
                <div className="ppu-corner ppu-br"></div>
              </div>
              <div className="ppu-status-row">
                <div className="ppu-status-dot"></div>
                <span className="ppu-status-text">Processing PDF…</span>
              </div>
              <div className="ppu-progress-wrap"><div className="ppu-progress-bar"></div></div>
            </div>
          </div>
        </div>

      </>
    );
  }

  return (
    <>
      <div className="uploads-container">
        {!uploadedImage ? (
          <div className="upload-area">
            <div 
              ref={dropAreaRef}
              className="upload-zone"
              onClick={openFileDialog}
            >
              <i className="bi bi-cloud-upload upload-icon"></i>
              <h5>Drop your floor plan here</h5>
              <p>or click to browse files</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileUpload}
                className="file-input"
                id="floorPlanInput"
              />
              <button className="upload-btn">
                <i className="bi bi-folder-plus me-2"></i>
                Choose File
              </button>
              <div className="supported-formats">
                <small>Supported formats: JPG, PNG, GIF, PDF (Max 10MB)</small>
              </div>
            </div>
          </div>
        ) : (
          <div className="image-editor">
            <div className="editor-controls">
              <div className="control-group">
                {!cropMode && (
                  <>
                    <button className="control-btn" onClick={handleZoomOut} title="Zoom Out">
                      <i className="bi bi-zoom-out"></i>
                    </button>
                    <span className="zoom-level">{Math.round(zoom * 100)}%</span>
                    <button className="control-btn" onClick={handleZoomIn} title="Zoom In">
                      <i className="bi bi-zoom-in"></i>
                    </button>
                    <button className="control-btn" onClick={handleRotate} title="Rotate 90° Clockwise">
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>
                  </>
                )}
              </div>

              <div className="control-group">
                {cropMode ? (
                  <>
                    <button 
                      className="control-btn" 
                      onClick={handleCropToggle} 
                      title="Cancel Crop"
                    >
                      <i className="bi bi-x"></i>
                      <span className="btn-text">Cancel</span>
                    </button>
                  </>
                ) : (
                  <>
                    {imageData?.isFromPdf && (
                      <button 
                        className="control-btn" 
                        onClick={handleBackToPdfSelection} 
                        title="Select Different Page"
                      >
                        <i className="bi bi-file-earmark-pdf"></i>
                        <span className="btn-text">Change Page</span>
                      </button>
                    )}
                    <button 
                      className="control-btn" 
                      onClick={handleCropToggle} 
                      title="Crop Tool"
                    >
                      <i className="bi bi-crop"></i>
                      <span className="btn-text">Crop</span>
                    </button>
                    <button className="control-btn danger" onClick={handleRemoveImage} title="Remove Image">
                      <i className="bi bi-trash"></i>
                    </button>
                  </>
                )}
              </div>
            </div>

            <div 
              ref={containerRef}
              className="image-viewer"
              style={{ 
                height: '600px',
                overflow: 'auto',
                border: '1px solid #00A19D',
                borderRadius: '8px',
                background: '#ffffff',
                position: 'relative',
                margin: '16px 0',
                cursor: getCursorStyle()
              }}
            >
              <div style={{
                position: 'relative',
                display: 'inline-block',
                minWidth: `${100 * zoom}%`,
                minHeight: `${100 * zoom}%`,
                padding: '20px'
              }}>
                <img
                  ref={imageRef}
                  src={uploadedImage}
                  alt="Floor Plan"
                  style={{
                    width: `${100 * zoom}%`,
                    height: 'auto',
                    display: 'block',
                    transform: `translate(${position.x}px, ${position.y}px)`
                  }}
                  draggable={false}
                />
              </div>
            </div>
            
            {onNext && !cropMode && !isNavigating && (
              <button
                className="next-btn"
                onClick={() => goNext(uploadedImage)}
                title="Continue to Configure"
              >
                <span className="next-text">Next</span>
                <i className="bi bi-arrow-right"></i>
              </button>
            )}
          </div>
        )}
      </div>

      {isNavigating && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(255,255,255,0.94)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
        }}>
          <div style={{ width: 42, height: 42, border: '4px solid #CFE3E2', borderTopColor: '#00A19D', borderRadius: '50%', animation: 'uploadsSpin 0.9s linear infinite' }} />
          <div style={{ color: '#1A1F36', fontWeight: 700, fontSize: '1rem' }}>Opening your project…</div>
          <div style={{ color: '#64748b', fontSize: '0.82rem' }}>Please wait, this takes a moment</div>
          <style>{'@keyframes uploadsSpin { to { transform: rotate(360deg); } }'}</style>
        </div>
      )}

      {showCropModal && !isNavigating && (
        <div className="crop-modal-overlay">
          <div className="crop-modal">
            <div className="crop-modal-header">
              <h4>
                <i className="bi bi-crop me-2"></i>
                Crop Image
              </h4>
              <div className="crop-modal-controls">
                <button className="control-btn" onClick={handleCropZoomOut} title="Zoom Out">
                  <i className="bi bi-zoom-out"></i>
                </button>
                <span className="zoom-level">{Math.round(cropZoom * 100)}%</span>
                <button className="control-btn" onClick={handleCropZoomIn} title="Zoom In">
                  <i className="bi bi-zoom-in"></i>
                </button>
                <button className="control-btn" onClick={handleCropZoomReset} title="Reset Zoom">
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
                <div className="separator"></div>
                <button 
                  className="control-btn" 
                  onClick={() => {
                    setCropMode(false);
                    setShowCropModal(false);
                    setCropArea({ x: 0, y: 0, width: 0, height: 0 });
                  }}
                  title="Cancel"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
            
            <div className="crop-modal-info">
              <span>
                <i className="bi bi-lightbulb me-1"></i>
                Draw a selection on the image, then drag corners/edges to adjust
              </span>
              {cropArea.width > 0 && cropArea.height > 0 && (
                <span className="crop-dimensions">
                  {Math.round(cropArea.width)} × {Math.round(cropArea.height)} px
                </span>
              )}
            </div>
            
            <div
              ref={cropModalRef}
              className="crop-modal-content"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              onTouchCancel={handleMouseUp}
              style={{ cursor: getCursorStyle(), touchAction: 'none' }}
            >
              <div 
                className="crop-image-container"
                style={{
                  transform: `scale(${cropZoom})`,
                  transformOrigin: 'center center'
                }}
              >
                <img
                  ref={cropImageRef}
                  src={originalImage || uploadedImage}
                  alt="Crop Preview"
                  draggable={false}
                />
                {renderCropOverlay()}
              </div>
            </div>
            
            <div className="crop-modal-footer">
              <button 
                className="control-btn"
                onClick={() => {
                  setCropMode(false);
                  setShowCropModal(false);
                  setCropArea({ x: 0, y: 0, width: 0, height: 0 });
                }}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </button>
              <button
                className="control-btn success"
                onClick={applyCropAndNext}
                disabled={cropArea.width === 0 || cropArea.height === 0 || isApplyingCrop}
              >
                <i className="bi bi-check-circle me-2"></i>
                {isApplyingCrop ? 'Applying…' : 'Crop & Next'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .upload-zone {
          border: 2px dashed #00A19D;
          border-radius: 12px;
          padding: 48px 24px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: rgba(0, 161, 157, 0.05);
        }

        .upload-zone:hover,
        .upload-zone.drag-over {
          border-color: #00857E;
          background: rgba(0, 161, 157, 0.1);
        }

        .upload-zone.drag-over {
          transform: scale(1.02);
        }

        .upload-icon {
          font-size: 3rem;
          color: #00A19D;
          margin-bottom: 16px;
        }

        .upload-zone h5 {
          color: #333333;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .upload-zone p {
          color: #666666;
          margin-bottom: 24px;
        }

        .file-input {
          display: none;
        }

        .upload-btn {
          background: #00A19D;
          color: #ffffff;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          transition: all 0.2s ease;
        }

        .upload-btn:hover {
          background: #00857E;
          transform: translateY(-1px);
        }

        .supported-formats {
          margin-top: 16px;
        }

        .supported-formats small {
          color: #999999;
        }

        .crop-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3000;
          animation: fadeIn 0.3s ease;
        }

        .crop-modal {
          background: #ffffff;
          border-radius: 12px;
          width: 95vw;
          height: 95vh;
          max-width: 1600px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 161, 157, 0.3);
          animation: slideIn 0.3s ease;
          border: 1px solid #00A19D;
        }

        .crop-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid #e5e5e5;
          background: #f9f9f9;
          border-radius: 12px 12px 0 0;
        }

        .crop-modal-header h4 {
          color: #333333;
          margin: 0;
          font-weight: 600;
          display: flex;
          align-items: center;
        }

        .crop-modal-header h4 i {
          color: #00A19D;
        }

        .crop-modal-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .separator {
          width: 1px;
          height: 24px;
          background: #e5e5e5;
          margin: 0 8px;
        }

        .crop-modal-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 24px;
          background: #f0f9f9;
          color: #666666;
          font-size: 0.85rem;
          border-bottom: 1px solid #e5e5e5;
        }

        .crop-modal-info i {
          color: #00A19D;
        }

        .crop-dimensions {
          color: #00A19D;
          font-weight: 500;
        }

        .crop-modal-content {
          flex: 1;
          overflow: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: #fafafa;
          position: relative;
          user-select: none;
        }

        .crop-image-container {
          position: relative;
          display: inline-block;
          transition: transform 0.2s ease-out;
        }

        .crop-image-container img {
          max-width: 100%;
          max-height: calc(95vh - 200px);
          display: block;
          object-fit: contain;
          user-select: none;
          -webkit-user-drag: none;
          pointer-events: none;
        }

        .crop-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e5e5e5;
          background: #f9f9f9;
          border-radius: 0 0 12px 12px;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from { 
            opacity: 0;
            transform: scale(0.95);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </>
  );
};

export default Uploads;